import { useEffect, useState } from 'react';
import { recordPageView } from './analytics/beacon';
import ModelViewer from './components/ModelViewer';
import PartSwitcher from './components/PartSwitcher';
import Dashboard from './pages/Dashboard';
import BlenderLab from './pages/BlenderLab';
import BallLab from './pages/BallLabV2';
import Box3D from './pages/Box3D';
import Configurator from './pages/Configurator';
import Heist from './pages/Heist';
import Gauntlet from './pages/Gauntlet';
import HumanoidLab from './pages/HumanoidLab';
import PhysicsPlayground from './pages/PhysicsPlayground';
import PenaltyShootout from './pages/PenaltyShootout';
import ProgressArt from './pages/ProgressArt';
import WorldCup from './pages/WorldCup';
import WreckingYard from './pages/WreckingYard';
import SleepingCity from './pages/SleepingCity';
import VideoBillboardGame from './pages/VideoBillboardGame';
import PortCrane from './pages/PortCrane';
import CastleLab from './pages/CastleLab';
import CharacterViewerPage from './components/CharacterViewerPage';
import { DEFAULT_MODEL } from './modelCatalog';
import './App.css';

type Page = 'dashboard' | 'viewer' | 'characterViewer' | 'configurator' | 'heist' | 'blenderLab' | 'progressArt' | 'worldcup' | 'ballLab' | 'penaltyShootout' | 'wreckingYard' | 'sleepingCity' | 'physicsPlayground' | 'gauntlet' | 'box3d' | 'humanoidLab' | 'videoBillboardGame' | 'portCrane' | 'castleLab';

// Lightweight path-based routing (no react-router): the SPA reads the URL on
// load and keeps it in sync, so routes can be opened directly.
const PAGE_PATH: Record<Page, string> = {
  dashboard: '/',
  configurator: '/configurator',
  viewer: '/viewer',
  characterViewer: '/character-viewer',
  heist: '/heist',
  blenderLab: '/blender-lab',
  progressArt: '/progress-art',
  worldcup: '/worldcup',
  ballLab: '/ball-lab',
  penaltyShootout: '/penalty-shootout',
  wreckingYard: '/wrecking-yard',
  sleepingCity: '/sleeping-city',
  physicsPlayground: '/physics-playground',
  gauntlet: '/gauntlet',
  box3d: '/box3d',
  humanoidLab: '/humanoid-lab',
  videoBillboardGame: '/video-characters',
  portCrane: '/port-crane',
  castleLab: '/castle',
};

const PAGE_TITLE: Record<Page, string> = {
  dashboard: '3D Lab — swapp1990',
  configurator: '3D Car Configurator',
  viewer: 'Car Parts Viewer',
  characterViewer: 'Character Lab — open asset viewer',
  heist: 'Heist Scene',
  blenderLab: 'Blender Character Lab',
  progressArt: 'Progress Artifact',
  worldcup: 'The Cup 26',
  ballLab: 'Box3D Sports Ball Lab',
  penaltyShootout: 'Penalty Shootout Lab',
  wreckingYard: 'Wrecking Yard — box3d demolition',
  sleepingCity: 'Sleeping City — box3d sleep at scale',
  physicsPlayground: 'Physics Playground',
  gauntlet: "The Gauntlet — Agents' Last Exam",
  box3d: 'three-box3d — 3D physics for three.js',
  humanoidLab: 'Humanoid Physics Lab',
  videoBillboardGame: 'Academy Hallway RPG',
  portCrane: 'Port Crane Rig — box3d',
  castleLab: 'The Castle — free-roam simulation',
};

const pathToPage = (path: string): Page => {
  if (path.startsWith('/video-characters')) return 'videoBillboardGame';
  if (path.startsWith('/port-crane')) return 'portCrane';
  if (path.startsWith('/castle')) return 'castleLab';
  if (path.startsWith('/box3d')) return 'box3d';
  if (path.startsWith('/humanoid-lab')) return 'humanoidLab';
  if (path.startsWith('/gauntlet')) return 'gauntlet';
  if (path.startsWith('/physics-playground')) return 'physicsPlayground';
  if (path.startsWith('/wrecking-yard')) return 'wreckingYard';
  if (path.startsWith('/sleeping-city')) return 'sleepingCity';
  if (path.startsWith('/penalty-shootout')) return 'penaltyShootout';
  if (path.startsWith('/worldcup')) return 'worldcup';
  if (path.startsWith('/ball-lab')) return 'ballLab';
  if (path.startsWith('/progress-art')) return 'progressArt';
  if (path.startsWith('/blender-lab')) return 'blenderLab';
  if (path.startsWith('/heist')) return 'heist';
  if (path.startsWith('/viewer')) return 'viewer';
  if (path.startsWith('/character-viewer')) return 'characterViewer';
  if (path.startsWith('/configurator')) return 'configurator';
  return 'dashboard';
};

function App() {
  const [page, setPage] = useState<Page>(() => pathToPage(window.location.pathname));
  const [currentModel, setCurrentModel] = useState(DEFAULT_MODEL);

  const navigate = (next: Page) => {
    setPage(next);
    if (window.location.pathname !== PAGE_PATH[next]) {
      window.history.pushState({}, '', PAGE_PATH[next]);
    }
  };

  // Sync state when the user uses the browser back/forward buttons.
  useEffect(() => {
    const onPop = () => setPage(pathToPage(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    document.title = PAGE_TITLE[page];
  }, [page]);

  // SwapAnalytics page-view beacon: fires on initial load and on every
  // SPA route change (navigate() / back-forward popstate both update `page`).
  useEffect(() => {
    recordPageView(window.location.pathname);
  }, [page]);

  if (page === 'dashboard') {
    return <Dashboard />;
  }

  if (page === 'configurator') {
    return <Configurator onBack={() => navigate('viewer')} />;
  }

  if (page === 'characterViewer') {
    return <CharacterViewerPage />;
  }

  if (page === 'heist') {
    return <Heist onBack={() => navigate('viewer')} />;
  }

  if (page === 'blenderLab') {
    return <BlenderLab onBack={() => navigate('viewer')} />;
  }

  if (page === 'progressArt') {
    return <ProgressArt onBack={() => navigate('viewer')} />;
  }

  if (page === 'worldcup') {
    return <WorldCup onBack={() => navigate('viewer')} />;
  }

  if (page === 'ballLab') {
    return <BallLab onBack={() => navigate('viewer')} />;
  }

  if (page === 'penaltyShootout') {
    return <PenaltyShootout onBack={() => navigate('viewer')} />;
  }

  if (page === 'wreckingYard') {
    return <WreckingYard onBack={() => navigate('viewer')} />;
  }

  if (page === 'sleepingCity') {
    return <SleepingCity onBack={() => navigate('viewer')} />;
  }

  if (page === 'physicsPlayground') {
    return <PhysicsPlayground onBack={() => navigate('viewer')} />;
  }

  if (page === 'gauntlet') {
    return <Gauntlet onBack={() => navigate('viewer')} />;
  }

  if (page === 'box3d') {
    return <Box3D onBack={() => navigate('viewer')} />;
  }

  if (page === 'humanoidLab') {
    return <HumanoidLab onBack={() => navigate('viewer')} />;
  }

  if (page === 'videoBillboardGame') {
    return <VideoBillboardGame onBack={() => navigate('viewer')} />;
  }

  if (page === 'portCrane') {
    return <PortCrane onBack={() => navigate('viewer')} />;
  }

  if (page === 'castleLab') {
    return <CastleLab onBack={() => navigate('dashboard')} />;
  }

  return (
    <div className="app">
      <header className="app-header">
        <h1 className="app-title">Car Parts Viewer</h1>
        <span className="app-subtitle">Meshy v6 Spike</span>
        <div className="header-actions">
          <button
            className="nav-button"
            onClick={() => navigate('configurator')}
            type="button"
          >
            Configurator
          </button>
          <button
            className="nav-button"
            data-testid="nav-heist"
            onClick={() => navigate('heist')}
            type="button"
          >
            Heist
          </button>
          <button
            className="nav-button"
            data-testid="nav-blender-lab"
            onClick={() => navigate('blenderLab')}
            type="button"
          >
            Blender Lab
          </button>
          <button
            className="nav-button"
            data-testid="nav-progress-art"
            onClick={() => navigate('progressArt')}
            type="button"
          >
            Progress Art
          </button>
          <button
            className="nav-button"
            data-testid="nav-worldcup"
            onClick={() => navigate('worldcup')}
            type="button"
          >
            World Cup
          </button>
          <button
            className="nav-button"
            data-testid="nav-ball-lab"
            onClick={() => navigate('ballLab')}
            type="button"
          >
            Ball Lab
          </button>
          <button
            className="nav-button"
            data-testid="nav-penalty-shootout"
            onClick={() => navigate('penaltyShootout')}
            type="button"
          >
            Penalty Shootout
          </button>
          <button
            className="nav-button"
            data-testid="nav-wrecking-yard"
            onClick={() => navigate('wreckingYard')}
            type="button"
          >
            Wrecking Yard
          </button>
          <button
            className="nav-button"
            data-testid="nav-sleeping-city"
            onClick={() => navigate('sleepingCity')}
            type="button"
          >
            Sleeping City
          </button>
          <button
            className="nav-button"
            data-testid="nav-physics-playground"
            onClick={() => navigate('physicsPlayground')}
            type="button"
          >
            Physics Playground
          </button>
          <button
            className="nav-button"
            data-testid="nav-gauntlet"
            onClick={() => navigate('gauntlet')}
            type="button"
          >
            The Gauntlet
          </button>
          <button
            className="nav-button"
            data-testid="nav-video-characters"
            onClick={() => navigate('videoBillboardGame')}
            type="button"
          >
            School Hallway
          </button>
          <button
            className="nav-button"
            data-testid="nav-humanoid-lab"
            onClick={() => navigate('humanoidLab')}
            type="button"
          >
            Humanoid Lab
          </button>
        </div>
      </header>

      <PartSwitcher currentModel={currentModel} onSelect={setCurrentModel} />

      <div className="viewer-container">
        <ModelViewer modelPath={currentModel} />
      </div>
    </div>
  );
}

export default App;
